import { COUNCILS, type CouncilSlug } from './councils';
import type { PlanningAlertCriteria } from '../types/alerts';

export const PENDING_PLANNING_ALERT_STORAGE_KEY = 'cityscope_pending_planning_alert_v1';
export const PENDING_PLANNING_ALERT_RESUME_PARAM = 'resumePlanningAlert';
export const PENDING_PLANNING_ALERT_TTL_MS = 30 * 60 * 1000;

export type PendingPlanningAlert = {
  version: 1;
  name: string;
  criteria: PlanningAlertCriteria;
  source: string;
  createdAt: number;
};

const storage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isCriteria = (value: unknown): value is PlanningAlertCriteria => {
  if (!value || typeof value !== 'object') return false;
  const criteria = value as Partial<PlanningAlertCriteria>;
  const area = criteria.area as Partial<PlanningAlertCriteria['area']> | undefined;
  return (
    Array.isArray(criteria.recordTypes) &&
    criteria.recordTypes.length > 0 &&
    Boolean(area) &&
    typeof area?.label === 'string' &&
    ((area.type === 'radius' && Number.isFinite(area.latitude) &&
      Number.isFinite(area.longitude) && Number.isFinite(area.radiusKm)) ||
     (area.type === 'council' && typeof area.councilSlug === 'string' &&
      Boolean(COUNCILS[area.councilSlug as CouncilSlug]?.publicEnabled)))
  );
};

export function clearPendingPlanningAlert(): void {
  storage()?.removeItem(PENDING_PLANNING_ALERT_STORAGE_KEY);
}

export function savePendingPlanningAlert(
  input: Omit<PendingPlanningAlert, 'version' | 'createdAt'>,
  now = Date.now(),
): PendingPlanningAlert | null {
  if (!isCriteria(input.criteria)) return null;
  const pending: PendingPlanningAlert = {
    version: 1,
    name: input.name.trim().slice(0, 80),
    criteria: input.criteria,
    source: input.source.trim().slice(0, 80) || 'planning_search',
    createdAt: now,
  };
  try {
    storage()?.setItem(PENDING_PLANNING_ALERT_STORAGE_KEY, JSON.stringify(pending));
    return pending;
  } catch {
    return null;
  }
}

export function loadPendingPlanningAlert(now = Date.now()): PendingPlanningAlert | null {
  const target = storage();
  if (!target) return null;
  try {
    const raw = target.getItem(PENDING_PLANNING_ALERT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingPlanningAlert>;
    if (
      parsed.version !== 1 ||
      typeof parsed.name !== 'string' ||
      typeof parsed.source !== 'string' ||
      typeof parsed.createdAt !== 'number' ||
      !isCriteria(parsed.criteria) ||
      parsed.createdAt > now ||
      now - parsed.createdAt > PENDING_PLANNING_ALERT_TTL_MS
    ) {
      clearPendingPlanningAlert();
      return null;
    }
    return parsed as PendingPlanningAlert;
  } catch {
    clearPendingPlanningAlert();
    return null;
  }
}

export function planningAlertResumeReturnTo(fallback = '/planning-updates'): string {
  if (typeof window === 'undefined') return `${fallback}?${PENDING_PLANNING_ALERT_RESUME_PARAM}=1`;
  const url = new URL(window.location.href);
  url.searchParams.set(PENDING_PLANNING_ALERT_RESUME_PARAM, '1');
  return `${url.pathname}${url.search}${url.hash}`;
}

export function consumePlanningAlertResumeMarker(): boolean {
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get(PENDING_PLANNING_ALERT_RESUME_PARAM) !== '1') return false;
  const hasPending = Boolean(loadPendingPlanningAlert());
  url.searchParams.delete(PENDING_PLANNING_ALERT_RESUME_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return hasPending;
}
